import {
  czMonthLabel,
  czWeekdayLabels,
  dateToWallDate,
  formatCzDate,
  formatCzDateTime,
  monthGridDays,
  parseCzDate,
  parseCzDateOrDateTime,
  todayPragueDate,
} from "@/lib/datetime";
import { cn } from "@/lib/utils";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type DropdownPlacement, useDropdownPlacement } from "./use-dropdown-placement";

/**
 * Custom date & datetime pickers — one date dialect for the whole app:
 * `dd.mm.yyyy`, a 24-hour clock, and (for datetimes) Europe/Prague.
 *
 * Both controls are drop-in replacements for the native
 * `<input type="date|datetime-local">` they retired: same controlled
 * string contract (`YYYY-MM-DD` / `YYYY-MM-DDTHH:mm`), so a callsite
 * swaps the element and keeps its state. The field is still a real
 * text `<input>` — typing `dd.mm.yyyy` works — with a calendar
 * popover hanging off a trigger button.
 */

const inputShell =
  "flex w-full items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring";
const textInput =
  "h-9 min-w-0 flex-1 bg-transparent px-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed";
const triggerBtn =
  "shrink-0 px-2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50";

// --- Calendar grid ----------------------------------------------------------

function MonthGrid({
  value,
  min,
  max,
  onPick,
}: {
  /** Highlighted day as `YYYY-MM-DD`, or null. */
  value: string | null;
  min?: string | undefined;
  max?: string | undefined;
  onPick: (wallDate: string) => void;
}) {
  const today = todayPragueDate();
  const anchor = value ?? today;
  const [year, setYear] = useState(Number(anchor.slice(0, 4)));
  const [month, setMonth] = useState(Number(anchor.slice(5, 7)) - 1);

  function step(delta: number) {
    const next = new Date(year, month + delta, 1);
    setYear(next.getFullYear());
    setMonth(next.getMonth());
  }

  const days = monthGridDays(year, month);
  const weekdays = czWeekdayLabels();

  return (
    <div className="w-64 p-2">
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => step(-1)}
          className="rounded p-1 hover:bg-accent hover:text-accent-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium capitalize">{czMonthLabel(year, month)}</span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => step(1)}
          className="rounded p-1 hover:bg-accent hover:text-accent-foreground"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-[0.65rem] uppercase text-muted-foreground">
        {weekdays.map((d) => (
          <span key={d} className="py-1">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d) => {
          const ds = dateToWallDate(d);
          const inMonth = d.getMonth() === month;
          const disabled = (min !== undefined && ds < min) || (max !== undefined && ds > max);
          const selected = ds === value;
          const isToday = ds === today;
          return (
            <button
              key={ds}
              type="button"
              disabled={disabled}
              aria-label={formatCzDate(ds)}
              aria-pressed={selected}
              onClick={() => onPick(ds)}
              className={cn(
                "h-8 rounded text-sm tabular-nums",
                "hover:bg-accent hover:text-accent-foreground",
                "disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent",
                !inMonth && "text-muted-foreground/50",
                isToday && !selected && "ring-1 ring-inset ring-border font-medium",
                selected && "bg-primary text-primary-foreground hover:bg-primary",
              )}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Shared outside-click / Escape closer for a popover, plus native-select
 *  style flip-up placement when the calendar can't fit below. */
function usePopover(): {
  open: boolean;
  setOpen: (v: boolean) => void;
  ref: React.RefObject<HTMLDivElement | null>;
  placement: DropdownPlacement;
} {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // MonthGrid (~17rem) + the datetime time row when present ≈ 21rem.
  const placement = useDropdownPlacement(ref, open, 332);
  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, ref, placement };
}

/** Panel placement class shared by both pickers. */
function panelPlacementClass(placement: DropdownPlacement): string {
  return placement === "up" ? "bottom-full mb-1" : "top-full mt-1";
}

// --- DatePicker -------------------------------------------------------------

type DatePickerProps = {
  /** Controlled value as `YYYY-MM-DD`. Omit for uncontrolled use. */
  value?: string;
  /** Initial value when uncontrolled. */
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Renders a hidden input so the picker plugs into a native `<form>`. */
  name?: string;
  id?: string;
  "aria-label"?: string;
  disabled?: boolean;
  required?: boolean;
  /** Inclusive bounds as `YYYY-MM-DD`. */
  min?: string;
  max?: string;
  className?: string;
  placeholder?: string;
};

export function DatePicker({
  value: controlledValue,
  defaultValue,
  onChange,
  name,
  id,
  "aria-label": ariaLabel,
  disabled,
  required,
  min,
  max,
  className,
  placeholder = "dd.mm.rrrr",
}: DatePickerProps) {
  const isControlled = controlledValue !== undefined;
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? "");
  const value = isControlled ? controlledValue : uncontrolled;

  const { open, setOpen, ref, placement } = usePopover();
  const [text, setText] = useState(formatCzDate(value));

  // Mirror external value changes back into the visible text.
  useEffect(() => {
    setText(formatCzDate(value));
  }, [value]);

  function emit(next: string) {
    if (!isControlled) setUncontrolled(next);
    onChange?.(next);
  }

  function commitText() {
    const t = text.trim();
    if (t === "") {
      if (value !== "") emit("");
      return;
    }
    const parsed = parseCzDate(t);
    if (parsed) {
      if (parsed !== value) emit(parsed);
    } else {
      // Unparseable — snap back to the last known-good value.
      setText(formatCzDate(value));
    }
  }

  return (
    <div ref={ref} className={cn("relative", className)}>
      {name && <input type="hidden" name={name} value={value} />}
      <div className={cn(inputShell, disabled && "opacity-50")}>
        <input
          id={id}
          aria-label={ariaLabel}
          type="text"
          inputMode="numeric"
          value={text}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText();
              setOpen(false);
            }
          }}
          className={textInput}
        />
        <button
          type="button"
          aria-label="Open calendar"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          className={triggerBtn}
        >
          <CalendarDays className="h-4 w-4" />
        </button>
      </div>
      {open && (
        <div
          className={cn(
            "absolute z-50 rounded-md border border-input bg-popover shadow-lg",
            panelPlacementClass(placement),
          )}
        >
          <MonthGrid
            key={value}
            value={value || null}
            min={min}
            max={max}
            onPick={(d) => {
              emit(d);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

// --- DateTimePicker ---------------------------------------------------------

const DEFAULT_TIME = "09:00";

type DateTimePickerProps = {
  /** Controlled value as `YYYY-MM-DDTHH:mm` (Prague wall-clock). */
  value?: string;
  /** Initial value when uncontrolled. */
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Renders a hidden input so the picker plugs into a native `<form>`. */
  name?: string;
  id?: string;
  "aria-label"?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  placeholder?: string;
};

export function DateTimePicker({
  value: controlledValue,
  defaultValue,
  onChange,
  name,
  id,
  "aria-label": ariaLabel,
  disabled,
  required,
  className,
  placeholder = "dd.mm.rrrr hh:mm",
}: DateTimePickerProps) {
  const isControlled = controlledValue !== undefined;
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? "");
  const value = isControlled ? controlledValue : uncontrolled;

  const { open, setOpen, ref, placement } = usePopover();
  const [text, setText] = useState(formatCzDateTime(value));

  useEffect(() => {
    setText(formatCzDateTime(value));
  }, [value]);

  const datePart = value.slice(0, 10);
  const timePart = value.length >= 16 ? value.slice(11, 16) : "";

  function emit(next: string) {
    if (!isControlled) setUncontrolled(next);
    onChange?.(next);
  }

  function commitText() {
    const t = text.trim();
    if (t === "") {
      if (value !== "") emit("");
      return;
    }
    // Date-only input is allowed — `dd.mm.yyyy` without a clock means
    // midnight (00:00). Useful for filters like TE from/to where the
    // user usually wants whole-day boundaries.
    const parsed = parseCzDateOrDateTime(t);
    if (parsed) {
      if (parsed !== value) emit(parsed);
    } else {
      setText(formatCzDateTime(value));
    }
  }

  function pickDay(wallDate: string) {
    emit(`${wallDate}T${timePart || DEFAULT_TIME}`);
  }

  function pickTime(hhmm: string) {
    const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (hh > 23 || mm > 59) return;
    const norm = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    emit(`${datePart || todayPragueDate()}T${norm}`);
  }

  return (
    <div ref={ref} className={cn("relative", className)}>
      {name && <input type="hidden" name={name} value={value} />}
      <div className={cn(inputShell, disabled && "opacity-50")}>
        <input
          id={id}
          aria-label={ariaLabel}
          type="text"
          value={text}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText();
              setOpen(false);
            }
          }}
          className={textInput}
        />
        <button
          type="button"
          aria-label="Open calendar"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          className={triggerBtn}
        >
          <CalendarDays className="h-4 w-4" />
        </button>
      </div>
      {open && (
        <div
          className={cn(
            "absolute z-50 rounded-md border border-input bg-popover shadow-lg",
            panelPlacementClass(placement),
          )}
        >
          <MonthGrid key={value} value={datePart || null} onPick={pickDay} />
          <div className="flex items-center gap-2 border-t border-input p-2">
            <label className="text-xs text-muted-foreground" htmlFor={`${id ?? "dtp"}-time`}>
              Time (hh:mm)
            </label>
            <input
              id={`${id ?? "dtp"}-time`}
              aria-label="Time"
              type="text"
              inputMode="numeric"
              maxLength={5}
              defaultValue={timePart || DEFAULT_TIME}
              key={timePart}
              onBlur={(e) => pickTime(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  pickTime((e.target as HTMLInputElement).value);
                }
              }}
              className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
      )}
    </div>
  );
}
